// AiUsagePill — compact "used/limit + credits" indicator.
//
// Mount this in the header of any AI tool. It calls
// GET /api/ai/usage?feature=<feature> on mount and shows:
//
//    [⚡ 3/5 · +12]
//
// where 3 = used this month, 5 = tier limit, +12 = pre-paid credits
// available (only rendered when > 0). Click opens the AiCreditsModal
// via the same global event used by the auto-quota-exceeded hook.
//
// The pill also subscribes to two events emitted elsewhere in the app:
//   • `makercut:ai-usage-bump` { feature } — increment used by 1 after
//     a successful AI call (lets pages refresh the count without a
//     second HTTP round-trip).
//   • `makercut:ai-credits-updated`        — re-fetch after a purchase.
//
// Colours: same ramp as the Account usage bars
//   < 70 %  emerald
//   70-89 % amber
//   ≥ 90 %  rose
// Pure-zero (limit = 0 = feature locked for this tier) shows a muted
// "🔒" pill that links to /pricing instead.
//
// Bilingual labels are inline (pt/en/es) — keeps the component
// self-contained, no i18n key pollution for a one-line UI element.

import { useEffect, useState, useCallback } from 'react';
import { Sparkles, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../i18n';

const LBL = {
  thisMonth:    { pt: 'usados este mês',     en: 'used this month',   es: 'usados este mes' },
  credits:      { pt: 'créditos extras',     en: 'extra credits',     es: 'créditos extras' },
  topUp:        { pt: 'Comprar créditos',    en: 'Buy credits',       es: 'Comprar créditos' },
  recharge:     { pt: 'Recarregar',          en: 'Top up',            es: 'Recargar' },
  locked:       { pt: 'IA não inclusa',      en: 'AI not included',   es: 'IA no incluida' },
  upgrade:      { pt: 'Ver planos',          en: 'View plans',        es: 'Ver planes' },
  loading:      { pt: '…',                   en: '…',                 es: '…' },
};

const tx = (key, lang) => (LBL[key] && (LBL[key][lang] || LBL[key].pt)) || '';

export default function AiUsagePill({ feature, className = '' }) {
  const { lang } = useI18n();
  const [data, setData] = useState(null);   // { used, limit, remaining, credit_balance }
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await api(`/api/ai/usage?feature=${encodeURIComponent(feature)}`);
      setData(r?.usage || null);
    } catch (_) {
      // Silent — pill just won't render. Better than blocking the tool
      // page on a flaky network call.
      setData(null);
    } finally {
      setLoaded(true);
    }
  }, [feature]);

  useEffect(() => {
    reload();
    const onBump = (e) => {
      if (!e?.detail?.feature || e.detail.feature === feature) reload();
    };
    const onCreditsUpdated = () => reload();
    window.addEventListener('makercut:ai-usage-bump',    onBump);
    window.addEventListener('makercut:ai-credits-updated', onCreditsUpdated);
    return () => {
      window.removeEventListener('makercut:ai-usage-bump',    onBump);
      window.removeEventListener('makercut:ai-credits-updated', onCreditsUpdated);
    };
  }, [reload, feature]);

  // Don't render anything until the first response arrives — prevents
  // a flash of "0/0" while the request is in flight.
  if (!loaded || !data) return null;

  const used  = Number(data.used)  || 0;
  const limit = Number(data.limit) || 0;
  const credits = Number(data.credit_balance) || 0;
  const pct = limit > 0 ? (used / limit) * 100 : 0;

  // Locked state — feature exists but this tier has 0 quota AND the
  // user has no credits either. Link to /pricing.
  if (limit === 0 && credits === 0) {
    return (
      <Link
        to="/pricing"
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-chunky uppercase tracking-wider bg-zinc-100 text-zinc-600 border border-zinc-200 hover:bg-zinc-200 transition-colors ${className}`}
        title={tx('locked', lang)}
      >
        <Lock className="w-3 h-3" />
        <span>{tx('locked', lang)}</span>
        <span className="text-zinc-500 underline underline-offset-2 ml-0.5">{tx('upgrade', lang)}</span>
      </Link>
    );
  }

  // Visual ramp — same colour code as the usage bars on /account.
  const remaining = Math.max(0, limit - used);
  const isOut   = remaining === 0;
  const lowMonthly = pct >= 90 || isOut;
  const midMonthly = pct >= 70 && pct < 90;

  // Cores: prioriza estado "out" se zerou e ainda assim tem créditos
  // (mensagem fica positiva — verde — pois ele PODE continuar usando).
  let tone;
  if (isOut && credits > 0)        tone = 'emerald-mixed';  // out of monthly, but has credits → still ok
  else if (isOut)                  tone = 'rose';            // out and no credits → blocked
  else if (lowMonthly)             tone = 'rose';
  else if (midMonthly)             tone = 'amber';
  else                              tone = 'emerald';

  const TONE = {
    emerald:        'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200',
    'emerald-mixed':'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200',
    amber:          'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200',
    rose:           'bg-rose-100 text-rose-800 border-rose-200 hover:bg-rose-200',
  }[tone];

  const openModal = () => {
    window.dispatchEvent(new CustomEvent('makercut:open-ai-credits'));
  };

  // Tooltip: full breakdown
  const tip = limit > 0
    ? `${used}/${limit} ${tx('thisMonth', lang)}${credits > 0 ? ` · ${credits} ${tx('credits', lang)}` : ''}`
    : `${credits} ${tx('credits', lang)}`;

  return (
    <button
      type="button"
      onClick={openModal}
      title={tip}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-chunky uppercase tracking-wider border transition-colors ${TONE} ${className}`}
    >
      <Sparkles className="w-3 h-3" />
      {limit > 0 ? (
        <span className="font-mono normal-case tracking-normal">
          {used}/{limit}
        </span>
      ) : null}
      {credits > 0 && (
        <span className="font-mono normal-case tracking-normal text-cyan-700 ml-0.5">
          +{credits}
        </span>
      )}
      {/* CTA — only show "+" when monthly is in the danger zone or out;
          otherwise the pill stays clean and unobtrusive. */}
      {(isOut || lowMonthly || credits > 0) && (
        <span className="ml-0.5 opacity-70">+</span>
      )}
    </button>
  );
}
